import { NavLink, Navigate, Outlet, Link } from "react-router-dom";
import {
  LayoutDashboard,
  Image as ImageIcon,
  MapPin,
  Package,
  GalleryHorizontal,
  PenSquare,
  Inbox,
  Instagram as InstagramIcon,
  Settings as SettingsIcon,
  LogOut,
  ExternalLink,
  Mountain,
} from "lucide-react";
import { useAdminAuth } from "./AuthContext";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/images", label: "Image Manager", icon: ImageIcon },
  { to: "/admin/destinations", label: "Destinations", icon: MapPin },
  { to: "/admin/packages", label: "Tour Packages", icon: Package },
  { to: "/admin/gallery", label: "Gallery", icon: GalleryHorizontal },
  { to: "/admin/blog", label: "Blog", icon: PenSquare },
  { to: "/admin/enquiries", label: "Enquiries", icon: Inbox },
  { to: "/admin/instagram", label: "Instagram", icon: InstagramIcon },
  { to: "/admin/settings", label: "Settings", icon: SettingsIcon },
];

export default function AdminLayout() {
  const { user, logout } = useAdminAuth();

  if (user === null) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-950 text-slate-400" data-testid="admin-loading">
        <div className="animate-pulse text-sm tracking-widest uppercase">Loading admin…</div>
      </div>
    );
  }
  if (user === false) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen flex bg-slate-100 dark:bg-slate-950" data-testid="admin-layout">
      <aside className="w-64 shrink-0 bg-slate-900 text-slate-300 flex flex-col fixed inset-y-0 left-0 z-40">
        <div className="h-16 flex items-center gap-2.5 px-5 border-b border-slate-800">
          <span className="h-8 w-8 grid place-items-center rounded-lg bg-emerald-500 text-white">
            <Mountain className="h-4 w-4" />
          </span>
          <div>
            <div className="text-white font-semibold text-sm leading-tight">RandZ Admin</div>
            <div className="text-[10px] text-slate-500 uppercase tracking-widest">Content Studio</div>
          </div>
        </div>
        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
          {NAV.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={`admin-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors ${
                  isActive ? "bg-emerald-500/15 text-emerald-400 font-medium" : "hover:bg-slate-800 hover:text-white"
                }`
              }
            >
              <Icon className="h-4 w-4" /> {label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-slate-800 space-y-1">
          <Link to="/" target="_blank" className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm hover:bg-slate-800 hover:text-white" data-testid="admin-view-site">
            <ExternalLink className="h-4 w-4" /> View website
          </Link>
          <button onClick={logout} className="w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-red-400 hover:bg-red-500/10" data-testid="admin-logout-btn">
            <LogOut className="h-4 w-4" /> Logout
          </button>
          <div className="px-3 pt-1 text-[11px] text-slate-600 truncate">{user.email}</div>
        </div>
      </aside>
      <main className="flex-1 ml-64 min-h-screen p-6 md:p-8 text-slate-900 dark:text-slate-100">
        <Outlet />
      </main>
    </div>
  );
}
